import {readNativeAiSceneLookup, type NativeAiSceneLookup} from './ai-scene-request';
import {readNativeAiSceneModifyRequest, type NativeAiSceneModifyRequest} from './ai-scene-modify-request';
import {sameAiSceneScope} from './ai-scene-host';
import type {NativeWorkScope} from './native-envelope';

export type AiSceneModifyHostRequest =
  | {method: 'GET'; path: '/__waka/ai-scene-modify/config'; body?: undefined; params?: undefined}
  | {method: 'POST'; path: '/__waka/ai-scene-modify'; body: NativeAiSceneModifyRequest; params?: undefined}
  | {method: 'POST'; path: '/__waka/ai-scene-modify/lookup'; body: NativeAiSceneLookup; params?: undefined};
const record = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
export function isAiSceneModifyHostRequest(raw: unknown): raw is AiSceneModifyHostRequest {
  if (!record(raw) || raw.params !== undefined || Object.keys(raw).some(key => !['method', 'path', 'body', 'params'].includes(key))) return false;
  if (raw.method === 'GET' && raw.path === '/__waka/ai-scene-modify/config') return raw.body === undefined;
  if (raw.method !== 'POST') return false;
  try {
    if (raw.path === '/__waka/ai-scene-modify') {readNativeAiSceneModifyRequest(raw.body); return true;}
    if (raw.path === '/__waka/ai-scene-modify/lookup') {readNativeAiSceneLookup(raw.body); return true;}
  } catch {return false;}
  return false;
}
export type AiSceneModifyReply = {
  id: string; requestId: string; scope: NativeWorkScope; revision: string; sourceHash: string;
  basis: {fps: number; width: number; height: number};
} & ({status: 'pending' | 'needs-review'} | {status: 'completed'; result: unknown});
export function readAiSceneModifyReply(raw: unknown, expected: NativeAiSceneLookup): AiSceneModifyReply {
  const fail = (): never => {throw Error('장면 수정 응답과 작업 정보가 다릅니다. 기존 편집은 유지됩니다.');};
  if (!record(raw)) return fail();
  const locator = readNativeAiSceneLookup({requestId: raw.requestId, scope: raw.scope});
  if (locator.requestId !== expected.requestId || !sameAiSceneScope(locator.scope, expected.scope)
    || typeof raw.id !== 'string' || !/^[a-f0-9]{64}$/.test(raw.id)
    || typeof raw.sourceHash !== 'string' || !/^[a-f0-9]{64}$/.test(raw.sourceHash)
    || typeof raw.revision !== 'string' || !Number.isFinite(Date.parse(raw.revision)) || new Date(raw.revision).toISOString() !== raw.revision
    || !record(raw.basis)) return fail();
  const {fps, width, height} = raw.basis;
  if (typeof fps !== 'number' || typeof width !== 'number' || typeof height !== 'number' || ![fps, width, height].every(value => Number.isFinite(value) && value > 0)) return fail();
  const base = {id: raw.id, ...locator, revision: raw.revision, sourceHash: raw.sourceHash, basis: {fps, width, height}};
  if (raw.status === 'pending' || raw.status === 'needs-review') return {...base, status: raw.status};
  if (raw.status === 'completed' && record(raw.result)) return {...base, status: 'completed', result: raw.result};
  return fail();
}
